import React from "react"
import { GatsbyImage } from "gatsby-plugin-image"
import styled from "styled-components"
import { Link } from "gatsby";
import parse from "html-react-parser"

const HeroWrapper = styled.section`
	flex: 2;
	max-width: 100%;
	margin-top: -4.5rem;
	padding-top: 4.5rem;
	background: #DAE0EE;
`;

const HeroInner = styled.div`
	margin: 0 auto;
	padding: 2rem 0;
	max-width: 1372px;
	display: flex;
	gap: 3rem;
	justify-content: center;
	align-items: center;
	position: relative;
	overflow: hidden;
	text-wrap: pretty;

	@media (max-width: 1250px) {
		align-items: center;
	}

	@media (max-width: 768px) {
		flex-direction: column;
		justify-content: stretch;
		gap: 0;
		padding-bottom: 0;
	}
`;

const HeroContent = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	position: relative;
	padding: 4rem 0 3rem 2rem;
	transform: translateX(4rem);
	flex: 1;

	@media (max-width: 1250px) {
		transform: none;
		padding: 2rem;
	}

	@media (max-width: 768px) {
		width: 100%;
		padding: 4rem 2rem 0;
	}

	> strong {
		font-weight: 900;
		margin-bottom: 0.75rem;
		display: block;
		font-size: 1.25rem;
	}
`;

const HeroTitle = styled.h1`
	font-size: 60px;
	font-weight: 700;
	line-height: 1;
	color: #24293C;
	margin-bottom: 2rem;

	strong {
		display: block;
		color: #6C7E99;
		font-size: 1em;
	}

	@media (max-width: 1250px) {
		font-size: 48px;
	}

	@media (max-width: 900px) {
		font-size: 36px;
	}
`;

const HeroText = styled.div`
	max-width: 500px;
	font-size: 1.25rem;
	color: #4E4F51;
	margin-bottom: 3rem;
	font-weight: 500;

	:last-child {
		margin-bottom: 0;
	}
`;

const HeroButton = styled(Link)`
	display: inline-block;
	padding: 1rem 2rem;
	background: #24293C;
	color: #fff;
	font-size: 1.25rem;
	text-decoration: none;
	margin-bottom: 1rem;

	&:hover,
	&:focus {
		background: #6C7E99;
	}
`

const HeroImage = styled.div`
	padding-top: 0;
	position: relative;
	overflow: hidden;
	appearance: none;
	border: none;
	background-color: transparent;
	transition: transform 0.5s;
	max-width: fit-content;
	flex: 1;
	@media (max-width: 768px) {
		margin: 0 1rem 1rem;
	}
`

const HeroFigure = styled.figure`
	padding-bottom: ${(props) => (props.awards > 0 ? "6%" : "0")};
	padding-left: ${(props) => (props.awards > 0 ? "13%" : "0")};
	padding-right: ${(props) => (props.awards > 1 ? "8%" : "0")};
	margin-bottom: 0;
`

const AwardWrapper = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
`

const AwardFigure = styled.figure`
	position: absolute;
	width: 30%;
	margin-bottom: 0;
	
	&:first-child {
		bottom: 0;
		left: 0;
	}

	&:nth-child(2) {
		bottom: 10%;
		right: 0;
	}

	&:nth-child(3) {
		top: 1rem;
		left: 0;
		transform: translateX(33%);
	}
`

const Hero = ({ data: { hero }}) => {
	const titleParts = hero.titleParts || {};
	const darkText = titleParts.darkText || "";
	const lightText = titleParts.lightText || "";

	const text = hero.text || "";
	const awards = hero.awards || [];
	const awardCount = awards.length;

	const button = hero.button || {};

	const image = hero.image || {};

	return (
		<HeroWrapper>
			<HeroInner>
				<HeroContent>
					{(darkText || lightText) && (
					<HeroTitle>
						{darkText && <div>{darkText}</div>}
						{lightText && <strong>{lightText}</strong>}
					</HeroTitle>
					)}
					{text && (
					<HeroText>{parse(text)}</HeroText>
					)}
					{button && button.label && button.url && (
						<HeroButton to={button.url}>{button.label}</HeroButton>
					)}
				</HeroContent>
				<HeroImage>
					{image && (
					<HeroFigure awards={awardCount}>
						<GatsbyImage
							image={image.node.localFile.childImageSharp.gatsbyImageData}
							alt={image.node.alt || ""}
							className="hero-image"
							placeholder="none"
						/>
					</HeroFigure>
					)}
					{awards && 
					<AwardWrapper>
						{awards.map(({award}, index) => (
						<AwardFigure key={index}>
							<GatsbyImage
								image={award.node.localFile.childImageSharp.gatsbyImageData}
								alt={award.node.alt || ""}
								className="award-image"
								placeholder="none"
							/>
						</AwardFigure>
						))}
					</AwardWrapper>
					}
				</HeroImage>
			</HeroInner>
		</HeroWrapper>
	)
}

export default Hero
